import { useContactFormStore } from '../store/contactFormStore';
import { IFormQueryResult, IFormItem, ICountryItem } from '../utils/interfaces';

/**
 * The API is hosted in AWS CloudFront. API requests are GET requests.
 * The CloudFront has a cache by path policy. Therefore, to avoid getting the same cached requests
 * from AEM preview, AEM publish and AWS pages, this method tries to add defunct params, 
 * so that the requests between each of these environments are cached separately.
 * 
 * @param originalQuery 
 * @returns Query with extra params
 */
const adaptQueryForPreviewHosts = (originalQuery: string): string => {
  const preview = 'preview';
  const publish = 'publish';
  const aws = 'aws';
  const extraParam = ';env='
  const currentHost = window.location.host;
  let hostParam;

  // Regex for preview domain
  const previewDomainRegex = /^preview-[\w-]+\.adobeaemcloud\.com$/;

  // Regex for publish domain
  const publishDomainRegex = /^publish-[\w-]+\.adobeaemcloud\.com$/;

  // Regex for AWS privacyhub domains (dev, stage, prod)
  const awsDomainRegex = /^privacyhub\.(dev|stage|prod)\.tccc-nextgen\.com$/;

  if (previewDomainRegex.test(currentHost)) {
    hostParam = encodeURIComponent(extraParam.concat(preview));
    return originalQuery.concat(hostParam);
  }

  if (publishDomainRegex.test(currentHost)) {
    hostParam = encodeURIComponent(extraParam.concat(publish));
    return originalQuery.concat(hostParam);
  }

  if (awsDomainRegex.test(currentHost)) {
    hostParam = encodeURIComponent(extraParam.concat(aws));
    return originalQuery.concat(hostParam);
  }
  return originalQuery;
};

const isQueryAmended = (originalQuery: string, amendedQuery: string): boolean => {
  return originalQuery !== amendedQuery;
};

const getGcpQueryPath = (query: string): string => {
  const basePath = '/api/content';
  const querypath = new URL(query).pathname;
  return basePath.concat(querypath);
}

const checkForBadParams = (query: string, folder: string) => {
  return (!query || !folder);
}

const getFormItems = (apiData: IFormQueryResult): Array<IFormItem> | false => {
  const formItems = apiData?.data?.contactFormData?.items;
  return formItems ?? false;
};

const getCountryItems = (apiData: IFormQueryResult): Array<ICountryItem> | false => {
  const countryItems = apiData?.data?.contactFormCountries?.items;
  return countryItems ?? false;
}

const loadRecaptcha = (recaptchaUrl: string): void => {
  const headSection = document.head;
  const reCaptchaScript = document.createElement('script');
  reCaptchaScript.src = recaptchaUrl;
  headSection.append(reCaptchaScript);
};

export const useInitialiseForm = (query: string, folder: string) => {
  const contactFormStore = useContactFormStore();
  const folderParam = ";folderPath=".concat(folder);
  const encodedFolderParam = encodeURIComponent(folderParam);
  const queryWithFolder = query.concat(encodedFolderParam);
  const completeQuery = adaptQueryForPreviewHosts(queryWithFolder);
  const finalQuery = isQueryAmended(queryWithFolder, completeQuery) ? completeQuery : getGcpQueryPath(queryWithFolder);

  const fetchFormData = () => {
    if (checkForBadParams(query, folder)) {
      contactFormStore.pathError = true;
      return;
    }

    fetch(finalQuery)
      .then((response) => response.json())
      .then((responseData) => {
        const formItems = getFormItems(responseData);
        const countryItems = getCountryItems(responseData);
        if (!formItems || !formItems.length || !countryItems || !countryItems.length) {
          contactFormStore.dataError = true;
          return;
        }
        contactFormStore.loadFormAndCountryItems(formItems, countryItems);
        loadRecaptcha(contactFormStore.getRecaptchaUrl);
      })
      .catch(() => {
        contactFormStore.apiError = true;
      });
  };

  return { fetchFormData };
};